import { cn } from '@/lib/utils'
import { useStudioLocale } from '@/i18n'

export interface ColumnDef<T> {
  key: keyof T & string
  header: string
  align?: 'left' | 'center' | 'right'
  width?: string
  render?: (value: T[keyof T], row: T) => React.ReactNode
}

interface DataTableProps<T> {
  columns: ColumnDef<T>[]
  data: T[]
  onRowClick?: (row: T) => void
  /** Overrides the localized default (`review.tableEmpty`). */
  emptyMessage?: string
  className?: string
}

export function DataTable<T extends object>({ columns, data, onRowClick, emptyMessage, className }: DataTableProps<T>) {
  const { strings } = useStudioLocale()
  const empty = emptyMessage ?? strings.businessScreens.review.tableEmpty
  if (data.length === 0) {
    return (
      <div className={cn('rounded-xl border border-dashed border-border bg-card p-8 text-center shadow-sm', className)}>
        <p className="text-sm font-medium text-foreground">{empty}</p>
      </div>
    )
  }

  return (
    <div className={cn('overflow-hidden rounded-xl border border-border bg-card shadow-sm', className)}>
      <div className="overflow-x-auto">
      <table className="w-full min-w-[720px]">
        <thead>
          <tr className="border-b border-border bg-muted/40">
            {columns.map((col) => (
              <th key={col.key} className={cn('px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground', col.align === 'right' && 'text-right', col.align === 'center' && 'text-center')} style={col.width ? { width: col.width } : undefined}>
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => (
            <tr
              key={i}
              onClick={() => onRowClick?.(row)}
              onKeyDown={(event) => {
                if (!onRowClick) return
                if (event.key !== 'Enter' && event.key !== ' ') return
                event.preventDefault()
                onRowClick(row)
              }}
              role={onRowClick ? 'button' : undefined}
              tabIndex={onRowClick ? 0 : undefined}
              className={cn('border-b border-border last:border-0 transition-colors', onRowClick && 'cursor-pointer hover:bg-accent/50 focus:bg-accent/50 focus:outline-none')}
            >
              {columns.map((col) => (
                <td key={col.key} className={cn('px-4 py-3 text-sm text-foreground', col.align === 'right' && 'text-right', col.align === 'center' && 'text-center')}>
                  {col.render ? col.render(row[col.key], row) : String(row[col.key] ?? '')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  )
}
